import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LoadMeeting, GetRoomStateEntity } from '../connection/meeting';
import { getCurrentShotIdEntity } from '../connection/shots';
import { RequestSyncedState } from '../connection/sync';
import {
	CustomRtmpData,
	getBroadcastServer,
	getEventsLegacyBroadcastEndpoint,
	getEventsSessionRtmpEndpoint,
	setCustomBroadcastEndpoint,
} from '../stores/actions/broadcasting';
import {
	getModeratedComments,
	getPresenterMessages,
	loadChatPosts,
	loadRoomChat,
} from '../stores/actions/chat';
import { getDefaultBackgrounds } from '../stores/actions/content';
import { loadGraphicsAction } from '../stores/actions/graphics';
import { loadNotifications } from '../stores/actions/home';
import {
	getInviteSsoDomains,
	getPendingJoinRequests,
	loadInvitations,
} from '../stores/actions/invitations';
import {
	getMedia,
	getMediaPagesForMeeting,
} from '../stores/actions/media';
import {
	loadOnsiteUsers,
	getProfile,
	getShowSessions,
	getShowSessionTypes,
	loadAdvancedAudioUsers,
} from '../stores/actions/session';
import { replaceState } from '../stores/actions/settings';
import { loadShots } from '../stores/actions/shots';
import { Meeting, ShowroomsTokens } from '../stores/reducers/session';
import { APP } from '../stores/session-status';
import { GreenroomState as GS } from '../stores/types/types';
import { getStorageItem } from '../utils/local-storage';
import { getStateFromSettingsJson } from '../utils/sync';
import {
	getAllAnimatedOverlays,
	getAllShotOverlays,
} from '../connection/graphics';
import { GetScheduledStream } from '../connection/scheduled-streams';
import { setScheduledStream } from '../stores/actions/meeting';
import { GetSpielbergBroadcast } from '../connection/broadcasting';
import store from '../stores/main';

interface LoadMeetingRoomParams {
	passedInToken: string;
	passedInMeeting: Meeting;
	embed: boolean;
	isReload: boolean;
	showroomsTokens?: ShowroomsTokens;
	successCallback?: () => void;
}

const useLoadCustomHook = () => {
	const dispatch = useDispatch();
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const appSignedInWith = useSelector((s: GS) => s.SessionReducer.appSignedInWith);
	const showSessionTypes = useSelector((s: GS) => s.SessionReducer.showSessionTypes);
	const homeMeeting = useSelector((s: GS) => s.HomeReducer?.homeMeeting);

	// Load Beehive scheduled stream data
	const loadScheduledStream = async (
		passedInToken: string,
		passedInMeetingId: number,
	) => {
		const scheduledStream = await GetScheduledStream(
			passedInToken,
			passedInMeetingId,
		);

		dispatch(setScheduledStream(scheduledStream));
	};

	const loadMeetingRoom = async ({
		passedInToken,
		passedInMeeting,
		embed,
		isReload,
		showroomsTokens,
		successCallback = () => { },
	}: LoadMeetingRoomParams): Promise<void> => {
		if (passedInMeeting) {
			/*
				It may seem overly complex to have both `userOnlyToken` and
				`embedOrUserToken`, but that makes it easier to see what is
				happening below since only certain routes will accept the embed
				token and this enables us to label which routes should be using
				which token for clarity.
			*/
			const userOnlyToken: string | null = !embed
				? passedInToken
				: null;
			const embedOrUserToken = passedInToken;

			let upToDateMeeting = passedInMeeting;

			if (isReload && userOnlyToken) {
				upToDateMeeting = await LoadMeeting(userOnlyToken, passedInMeeting.meeting);
			}

			if (upToDateMeeting) {
				const useSpielberg = store.getState().SettingsReducer.featureFlags.use_spielberg;
				const { meeting: meetingRowId } = upToDateMeeting;
				// Load Beehive data
				RequestSyncedState(meetingRowId, embedOrUserToken);

				const roomStateEntity = await GetRoomStateEntity(embedOrUserToken, meetingRowId);
				if (roomStateEntity) {
					dispatch({ type: roomStateEntity.entityType, payload: roomStateEntity });
				}

				const currentShotIdEntity = await getCurrentShotIdEntity(embedOrUserToken, meetingRowId);
				if (currentShotIdEntity) {
					dispatch({ type: currentShotIdEntity.entityType, payload: currentShotIdEntity });
				}

				if (useSpielberg) {
					const broadcastEntity = await GetSpielbergBroadcast(embedOrUserToken, meetingRowId);
					if (broadcastEntity) {
						dispatch({ type: broadcastEntity.entityType, payload: broadcastEntity });
					}
				}

				// Load Beehive overlay data
				const allShotOverlaysEntity = await getAllShotOverlays(
					embedOrUserToken,
					meetingRowId,
				);
				const allAnimatedOverlaysEntity = await getAllAnimatedOverlays(
					embedOrUserToken,
					meetingRowId,
				);

				if (allShotOverlaysEntity) {
					dispatch({
						type: allShotOverlaysEntity.entityType,
						payload: allShotOverlaysEntity,
					});
				}

				if (allAnimatedOverlaysEntity) {
					dispatch({
						type: allAnimatedOverlaysEntity.entityType,
						payload: allAnimatedOverlaysEntity,
					});
				}

				loadScheduledStream(embedOrUserToken, meetingRowId);

				// Load API data
				// the routes below have been set up to manually accept the embed token
				dispatch(loadShots(embedOrUserToken, meetingRowId));
				dispatch(getMedia(embedOrUserToken, meetingRowId));
				dispatch(getMediaPagesForMeeting(embedOrUserToken, meetingRowId));
				dispatch(loadInvitations(embedOrUserToken, meetingRowId));
				dispatch(loadGraphicsAction(embedOrUserToken, meetingRowId));
				dispatch(loadOnsiteUsers(embedOrUserToken, meetingRowId));
				dispatch(loadAdvancedAudioUsers(embedOrUserToken, meetingRowId));

				// the routes below have NOT been set up to accept the embed token
				// (the embed view does not need the data loaded from these routes)
				if (!embed && userOnlyToken) {
					dispatch(getShowSessions(userOnlyToken, meetingRowId));

					// Static items that are never changed from initial load
					if (!isReload) {
						dispatch(getDefaultBackgrounds(userOnlyToken));
					}
				}

				/*
					API data that only needs to be retrieved when reconnecting to socket.
					-
					The idea behind adding the `isReload` check is this `loadMeetingRoom`
					method is largely copying functionality that used to happen in the
					`meeting-room.tsx` component, yet when reconnecting to Socket, we
					also needs to reload items like chats that are set up in different
					components like `chat-main.tsx`.
				*/
				if (isReload && userOnlyToken) {
					const appSignedInWithStorage = getStorageItem('app-signed-in-with');

					if (appSignedInWithStorage) {
						dispatch(getProfile(
							userOnlyToken,
							appSignedInWithStorage,
							showroomsTokens?.accessToken,
						));
					}

					// don't start a Bullman server if the spielberg flag is on
					if (!useSpielberg) {
						dispatch(getBroadcastServer(userOnlyToken, meetingRowId));
					}

					dispatch(loadNotifications(userOnlyToken));
					dispatch(loadChatPosts(userOnlyToken, meetingRowId));
					dispatch(loadRoomChat(userOnlyToken, meetingRowId));
					dispatch(getPendingJoinRequests(userOnlyToken, meetingRowId));

					// Replace state with `upToDateMeeting`
					dispatch(replaceState(
						getStateFromSettingsJson(upToDateMeeting.settings_json),
					));
				}

				successCallback();
			}
		}
	};

	const loadShowDetails = useCallback((): void => {
		const homeMeetingId = homeMeeting?.meeting;

		if (token && homeMeetingId) {
			dispatch(loadInvitations(token, homeMeetingId));
			dispatch(getShowSessions(token, homeMeetingId));

			if (!showSessionTypes || showSessionTypes.length === 0) {
				dispatch(getShowSessionTypes(token));
			}

			dispatch(loadShots(token, homeMeetingId));
			dispatch(getMedia(token, homeMeetingId));
			dispatch(replaceState(JSON.parse(homeMeeting.settings_json)));
			dispatch(loadGraphicsAction(token, homeMeetingId));

			if (host) {
				// don't start a Bullman server if the spielberg flag is on
				const useSpielberg = store.getState().SettingsReducer.featureFlags.use_spielberg;
				if (!useSpielberg) {
					dispatch(getBroadcastServer(token, homeMeetingId));
				}

				if (appSignedInWith === APP.EVENTS) {
					dispatch(
						getEventsSessionRtmpEndpoint(
							token,
							homeMeeting.events_session_uuid,
							homeMeeting.language,
						),
					);
					dispatch(getInviteSsoDomains(token, homeMeetingId));
				} else if (appSignedInWith === APP.EVENTS_LEGACY) {
					dispatch(
						getEventsLegacyBroadcastEndpoint(token, homeMeeting.channel),
					);
				}

				if (homeMeeting.rtmp_endpoint) {
					const customUrl = homeMeeting.rtmp_endpoint.substring(
						0,
						homeMeeting.rtmp_endpoint.lastIndexOf('/'),
					);
					const customStreamKey = homeMeeting.rtmp_endpoint.substring(
						homeMeeting.rtmp_endpoint.lastIndexOf('/') + 1,
					);

					const customRtmpEndpoint = {
						name: homeMeeting.rtmp_endpoint_name ?? 'Custom Destination',
						url: customUrl,
						stream_key: customStreamKey,
					};

					const data = {} as CustomRtmpData;
					data.endpoint = customRtmpEndpoint;
					data.useCustomEndpoint = !!homeMeeting.use_custom_rtmp_endpoint;

					dispatch(setCustomBroadcastEndpoint(data));
				}

				loadScheduledStream(token, homeMeetingId);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appSignedInWith, homeMeeting, host, showSessionTypes, token]);

	const loadLegacyModeratedChats = useCallback((): void => {
		if (token && meeting?.brandlive_event) {
			dispatch(getModeratedComments(token, meeting.brandlive_event));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting, token]);

	const loadModeratedChats = useCallback((): void => {
		if (token && meeting?.meeting_id) {
			dispatch(getPresenterMessages(token, meeting.meeting_id));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meeting, token]);

	return {
		loadMeetingRoom,
		loadShowDetails,
		loadLegacyModeratedChats,
		loadModeratedChats,
	};
};

export default useLoadCustomHook;
